import React from 'react';
import logLogInPageSignupButtonTapped from '@alltrails/analytics/events/logLogInPageSignupButtonTapped';
import Button from '@alltrails/shared/denali/components/Button';
import CustomProvider from 'components/CustomProvider';
import SectionDivider from 'components/shared/SectionDivider';
import TermsOfService from 'components/TermsOfService';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { ProPageStrings } from 'react_components/mixins/shared/string_constants_mixin';
import { defineMessages, FormattedMessage } from 'react-intl';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { appleHandler, facebookHandler, googleHandler } from 'utils/OAuthHandlers';
import AltLoginsForm from 'components/AltLoginsForm';
import User from '@alltrails/shared/types/User';
import EmailPasswordLogin from 'components/EmailPasswordLogin';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import * as baseStyles from '../../../styles/baseStyles.module.scss';
import * as formStyles from '../styles/styles.module.scss';
import Wrapper from '../Wrapper';
import * as styles from './styles/styles.module.scss';

const messages = defineMessages({
  forgotPassword: {
    defaultMessage: 'Forgot your password?'
  }
});

const TEXT = { ...ProPageStrings.loginText, ...ProPageStrings.loginForm };

type Props = {
  handleLoginSuccess: ({ user }: { user: User }) => void;
  handleRegisterClick?: any;
  returnTo?: string; // url
};

function LoginForm({ handleLoginSuccess, handleRegisterClick, returnTo = '/' }: Props) {
  const {
    formattedDefaultMessages: { forgotPassword }
  } = useFormatMessage(messages);

  const languageRegionCode = useLanguageRegionCode();

  function loginSuccessCallback(data: any) {
    if (handleLoginSuccess) {
      return handleLoginSuccess(data);
    }

    window.location.href = data.welcome_notice ? `${returnTo + (returnTo.includes('?') ? '&' : '?')}welcome=${data.welcome_notice}` : returnTo;
    return undefined;
  }

  function handleGoogle(response: any) {
    googleHandler(
      response,
      languageRegionCode,
      loginSuccessCallback,
      () => {},
      () => {}
    );
  }

  function handleFacebook(response: any) {
    facebookHandler(
      response,
      languageRegionCode,
      loginSuccessCallback,
      () => {},
      () => {}
    );
  }

  function handleApple() {
    appleHandler();
  }

  function renderFooter() {
    const href = LanguageSupportUtil.wrapUrlSafe('/signup', languageRegionCode);

    return (
      <>
        <TermsOfService />
        <div className={`${baseStyles.action}`}>
          <FormattedMessage defaultMessage="Don't have an account?" />{' '}
          <a
            className={baseStyles.route}
            href={href}
            onClick={e => {
              logLogInPageSignupButtonTapped();

              if (handleRegisterClick) {
                e.preventDefault();
                handleRegisterClick(e);
              }

              return true;
            }}
          >
            <FormattedMessage defaultMessage="Sign up for free" />
          </a>
        </div>
      </>
    );
  }

  return (
    <Wrapper foot={renderFooter()} text={TEXT}>
      <div className={baseStyles.section}>
        <EmailPasswordLogin handleLoginSuccess={loginSuccessCallback} returnTo={returnTo} />
        <div className={`${formStyles.action} ${styles.forgotPassword}`}>
          <Button
            text={forgotPassword}
            linkInfo={{ href: LanguageSupportUtil.wrapUrlSafe('/users/password/new', languageRegionCode) }}
            onClick={() => {
              /* do nothing */
            }}
            testId="login-forgot-password"
            variant="flat"
            width="full"
          />
        </div>
      </div>
      <SectionDivider classes={styles.sectionDivider}>
        <FormattedMessage defaultMessage="or" />
      </SectionDivider>
      <AltLoginsForm appleLoginHandler={handleApple} facebookLoginHandler={handleFacebook} googleLoginHandler={handleGoogle} />
    </Wrapper>
  );
}

const LoginFormWrapper = (props: Props): JSX.Element => (
  <CustomProvider>
    <LoginForm {...props} />
  </CustomProvider>
);

export default LoginFormWrapper;
